import React, { useRef, useEffect, useState } from 'react';
import 'video.js/dist/video-js.min.css';
import videojs from 'video.js';

import RecordRTC from 'recordrtc';
import 'webrtc-adapter';

import 'videojs-record/dist/css/videojs.record.css';
import 'videojs-record/dist/videojs.record.js';
import 'videojs-record/dist/plugins/videojs.record.ts-ebml.js';
import Player from 'video.js/dist/types/player';

import { styled, Box, Typography, SvgIcon, Stack } from '@mui/material';
import { useIsMobileBrowser, useIsMobileView, usePlatform } from 'src/hooks';
import { SoundMeter } from 'src/components';
import { ReactComponent as VideoCameraIcon } from '../../../assets/video-camera-icon.svg';
import { VideoInterviewQuestionStatus } from '../../../types';

const Timer = styled(Box)(() => ({
  transform: 'translate(-50%, -50%)',
  animation: 'anim 1s ease-in-out infinite',
  '@keyframes anim': {
    '0%': {
      transform: 'scale(1)',
      opacity: 1,
    },
    '100%': {
      transform: 'scale(8)',
      opacity: 0,
    },
  },
}));

const VideoRecordWrapper = styled('div')({
  position: 'relative',
  '.video-js': {
    borderRadius: '10px',
    width: 511,
    height: 279,
    '& .vjs-progress-control': {
      marginRight: '15px',
    },
    '& .vjs-control-bar': {
      background:
        'linear-gradient(0deg, rgba(0,0,0,1) 18%, rgba(255,255,255,0) 100%)',
    },
  },
  '.vjs-record.vjs-device-button.vjs-control': {
    display: 'none',
  },
  '.vjs-tech': {
    borderRadius: '10px',
    objectFit: 'cover',
  },
  ['@media (max-width: 1200px)']: {
    width: '100%',
    '.video-js': {
      width: '100%',
    },
  },
  ['@media (max-width: 820px)']: {
    display: 'flex',
    justifyContent: 'center',
    background: '#F4FBFE',
    '.video-js': {
      height: 377,
      width: 320,
    },
  },
  ['@media (max-width: 425px)']: {
    background: '#000',
    margin: '0 -24px',
    width: 'calc(100% + 48px)',
    padding: '6px 0',
    '.video-js': {
      height: 430,
      width: 240,
    },
  },
});

const getPlatformSteps = (isAndroid: boolean, isIOS: boolean): string[] => {
  if (isAndroid) {
    return [
      'Open Settings',
      'Tap Apps or Apps & notifications',
      'Select Chrome (or your browser)',
      'Tap Permissions',
      'Tap Camera and choose Allow',
    ];
  }

  if (isIOS) {
    return [
      'Open Settings',
      'Scroll down and tap Safari (or your browser)',
      'Tap Camera',
      'Select Allow',
    ];
  }

  return [];
};

// [FIX #7] Helper to determine the best supported video MIME type
const getSupportedVideoMimeType = (isIOSChrome: boolean): string => {
  if (isIOSChrome) {
    // iOS Chrome: prefer mp4, fall back to webm, then let browser decide
    if (typeof MediaRecorder !== 'undefined') {
      if (MediaRecorder.isTypeSupported('video/mp4')) return 'video/mp4';
      if (MediaRecorder.isTypeSupported('video/webm')) return 'video/webm';
    }
    return 'video/mp4'; // Let MediaRecorder attempt mp4 as last resort
  }
  // Non-iOS-Chrome: prefer webm for better browser support
  if (typeof MediaRecorder !== 'undefined') {
    if (MediaRecorder.isTypeSupported('video/webm;codecs=vp8,opus'))
      return 'video/webm;codecs=vp8,opus';
    if (MediaRecorder.isTypeSupported('video/webm;codecs=vp8'))
      return 'video/webm;codecs=vp8';
    if (MediaRecorder.isTypeSupported('video/webm')) return 'video/webm';
    if (MediaRecorder.isTypeSupported('video/mp4')) return 'video/mp4';
  }
  return 'video/webm';
};

// [FIX #7] Helper to determine the best supported audio MIME type matching the container
const getSupportedAudioMimeType = (
  videoMimeType: string,
): string | undefined => {
  if (videoMimeType.includes('mp4')) {
    if (
      typeof MediaRecorder !== 'undefined' &&
      MediaRecorder.isTypeSupported('audio/mp4')
    ) {
      return 'audio/mp4';
    }
    return undefined; // Let RecordRTC pick the default
  }
  // For webm containers, audio is included in the video container
  return undefined;
};

interface IProps {
  onReady: (player: Player) => void;
  deviceIsReady: boolean;
  deviceIsBlocked: boolean;
  duration: number;
  questionStatus: VideoInterviewQuestionStatus;
  recordingStream?: MediaStream | null;
}

export const VideoJSComponent: React.FC<IProps> = ({
  onReady,
  deviceIsReady,
  deviceIsBlocked,
  duration,
  questionStatus,
  recordingStream,
}) => {
  const { isAndroid, isIOS, isIOSChrome } = usePlatform();
  const isMobileView = useIsMobileView();
  const isMobile = useIsMobileBrowser();
  const videoRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<Player | null>(null);
  const [loadingTime, setLoadingTime] = useState(3);
  const [retryCount, setRetryCount] = useState(0);

  // [FIX #3] Apply bitrate configuration for ALL platforms, not just iOS.
  // Without explicit bitrate limits, browsers may use very high defaults that
  // cause frame drops and black frames on lower-powered devices.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const DefaultMediaRecorder = window.MediaRecorder;
    const kb = 8 * 1024;

    // Platform-specific bitrate tuning
    let audioBitRate: number;
    let videoBitRate: number;

    if (isIOSChrome) {
      // iOS Chrome: lowest bitrates to prevent memory issues
      audioBitRate = 32 * kb;
      videoBitRate = 200 * kb;
    } else if (isIOS) {
      // iOS Safari: moderate bitrates
      audioBitRate = 100 * kb;
      videoBitRate = 500 * kb;
    } else {
      // Desktop / Android: comfortable defaults
      audioBitRate = 128 * kb;
      videoBitRate = 1000 * kb;
    }

    window.MediaRecorder = class extends DefaultMediaRecorder {
      constructor(stream: MediaStream, options?: MediaRecorderOptions) {
        super(stream, {
          ...options,
          audioBitsPerSecond: options?.audioBitsPerSecond || audioBitRate,
          videoBitsPerSecond: options?.videoBitsPerSecond || videoBitRate,
        });
      }
    };
  }, [isIOSChrome, isIOS]);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval> | undefined;
    if (questionStatus === VideoInterviewQuestionStatus.LOADING) {
      interval = setInterval(() => {
        setLoadingTime((prevTime) => {
          if (prevTime === 0) {
            clearInterval(interval);
            return 0;
          }
          return prevTime - 1;
        });
      }, 1000);
    } else {
      setLoadingTime(3);
    }

    return () => clearInterval(interval);
  }, [questionStatus]);

  useEffect(() => {
    const attemptGetDevice = () => {
      try {
        (playerRef.current as any)?.record().getDevice();
      } catch (err) {
        console.error('Error getting device:', err);
        if (isIOS && retryCount < 3) {
          setTimeout(() => {
            setRetryCount((prev) => prev + 1);
          }, 1000);
        }
      }
    };

    if (!playerRef.current) {
      createPlayer();
    } else if (playerRef.current) {
      attemptGetDevice();
    }
  }, [videoRef, retryCount]);

  useEffect(() => {
    return disposePlayer;
  }, [playerRef]);

  // [FIX #11] Guard player disposal against active recording state.
  // Previously, a duration change could destroy an active recording.
  useEffect(() => {
    if (playerRef.current && duration) {
      const player = playerRef.current as any;
      const isRecording = player?.record?.()?.isRecording?.();
      if (isRecording) {
        // Don't recreate the player while recording — it would kill the stream
        console.warn(
          'Duration changed while recording; deferring player recreation',
        );
        return;
      }
      disposePlayer();
      createPlayer();
    }
  }, [duration]);

  const createPlayer = () => {
    const isWebmSupported =
      typeof MediaRecorder !== 'undefined' &&
      MediaRecorder.isTypeSupported('video/webm');

    // [FIX #7] Determine the best supported MIME types for this platform
    const videoMimeType = getSupportedVideoMimeType(isIOSChrome);
    const audioMimeType = getSupportedAudioMimeType(videoMimeType);

    // Only use ts-ebml for webm recordings (it fixes duration metadata)
    const useConvertEngine = isWebmSupported && videoMimeType.includes('webm');

    const recordPluginConfig: Record<string, any> = {
      audio: true,
      video: true,
      maxLength: duration * 60,
      debug: true,
      displayMilliseconds: false,
      videoRecorderType: RecordRTC.MediaStreamRecorder,
      videoMimeType,
      ...(audioMimeType ? { audioMimeType } : {}),
      ...(useConvertEngine ? { convertEngine: 'ts-ebml' } : {}),
    };

    // iOS Chrome specific optimizations
    if (isIOSChrome) {
      // Do NOT set timeSlice for mp4 recordings — mp4 chunks cannot be naively
      // concatenated, causing RecordRTC to produce a corrupt Blob that plays back
      // for only ~1 second regardless of actual recording length.

      // Disable the separate audio recorder so videojs-record uses a single
      // MediaStreamRecorder that captures audio+video together in the mp4 container.
      // Without this, videojs-record creates two recorders and tries to merge them,
      // which fails on iOS WebKit and produces a truncated blob.
      recordPluginConfig.audioRecorderType = false;
      recordPluginConfig.audioMimeType = false;

      recordPluginConfig.bufferSize = 2048;
      recordPluginConfig.sampleRate = 22050;
      recordPluginConfig.disableLogs = false;
      recordPluginConfig.recorderType = RecordRTC.MediaStreamRecorder;
    }

    const videoJsOptions = {
      autoplay: true,
      controls: false,
      bigPlayButton: false,
      width: isMobile ? 320 : 640,
      height: isMobile ? 240 : 480,
      fluid: false,
      controlBar: {
        playToggle: true,
        recordToggle: false,
        fullscreenToggle: false,
      },
      plugins: {
        record: recordPluginConfig,
      },
    };

    const videoElement = document.createElement('video-js');

    videoElement.className = 'video-js vjs-default-skin';

    // Add inline playback attributes for mobile
    if (isIOS || isMobile) {
      videoElement.setAttribute('playsinline', 'true');
      videoElement.setAttribute('webkit-playsinline', 'true');
    }

    (videoRef.current as any).appendChild(videoElement);

    const player = ((playerRef.current as any) = videojs(
      videoElement,
      videoJsOptions,
      () => {
        const version_info =
          'Using video.js ' +
          (videojs as any).VERSION +
          ' with videojs-record ' +
          videojs.getPluginVersion('record') +
          ', recordrtc ' +
          RecordRTC.version +
          ' and React ' +
          React.version;
        videojs.log(version_info);

        onReady(player);
      },
    ));
  };

  const disposePlayer = () => {
    const player = playerRef.current;

    if (player && !(player as any).isDisposed()) {
      try {
        (player as any).dispose();
      } catch (err) {
        // iOS WebKit can detach the video element from DOM when a MediaStream ends,
        // causing Video.js's internal removeChild to throw NotFoundError.
        console.warn('VideoJSComponent: dispose error (iOS WebKit)', err);
      }
      playerRef.current = null;
    }
  };

  const steps = getPlatformSteps(isAndroid, isIOS);

  return (
    <VideoRecordWrapper data-vjs-player>
      <div ref={videoRef} />
      {!deviceIsReady && (
        <Box
          sx={{
            color: 'white',
            position: 'absolute',
            top: '50%',
            left: '50%',
            transform: 'translate(-50%, -50%)',
            textAlign: 'center',
            width: '100%',
            px: 2,
          }}
        >
          <SvgIcon
            inheritViewBox
            component={VideoCameraIcon}
            sx={(theme) => ({
              width: 20,
              path: {
                fill: theme.palette.brand.accent,
              },
            })}
          />
          {deviceIsBlocked ? (
            <>
              <Typography variant="h3" mb={2}>
                Your camera is blocked
              </Typography>
              <Typography variant="body2" mb={3}>
                {isIOS
                  ? 'Please grant camera and microphone permissions in your browser settings'
                  : isMobile
                  ? 'Please grant camera and microphone permissions to continue'
                  : "Click the camera icon in your browser's address bar to configure permissions so we can see and hear you."}
              </Typography>
            </>
          ) : (
            <Typography variant="body2" mb={3}>
              Please allow access <br />
              to your camera and microphone <br />
              so we can see and hear you
            </Typography>
          )}

          {isMobileView && (
            <>
              {steps.length > 0 ? (
                <Box
                  component="ol"
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    gap: 1,
                    listStyle: 'none',
                    p: 0,
                    m: 'auto',
                    maxWidth: 320,
                  }}
                >
                  {steps.map((step, index) => (
                    <Stack
                      key={index}
                      direction="row"
                      alignItems="center"
                      sx={{
                        background: 'rgba(255, 255, 255, 0.06)',
                        backdropFilter: 'blur(4px)',
                        px: 2,
                        py: 1,
                        borderRadius: 3,
                        maxWidth: '100%',
                        width: '100%',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
                        border: '1px solid rgba(255, 255, 255, 0.1)',
                      }}
                    >
                      <Box
                        sx={(theme) => ({
                          backgroundColor: theme.palette.brand?.accent,
                          color: '#fff',
                          width: 28,
                          height: 28,
                          fontSize: 14,
                          fontWeight: 'bold',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          mr: 2,
                          flexShrink: 0,
                          boxShadow: '0 0 4px rgba(0,0,0,0.4)',
                        })}
                      >
                        {index + 1}
                      </Box>
                      <Typography variant="caption" sx={{ textAlign: 'left' }}>
                        {step}
                      </Typography>
                    </Stack>
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" mt={2}>
                  Unsupported platform. Please check your browser settings.
                </Typography>
              )}
            </>
          )}
        </Box>
      )}
      {/* PLP-2321: Keep this Box always mounted instead of conditionally rendering.
          On iOS (WebKit), React's removeChild throws NotFoundError when unmounting
          an element with an active infinite CSS animation because WebKit composites
          it to a GPU layer. Use display:none to hide it instead. */}
      <Box
        sx={{
          color: 'white',
          position: 'absolute',
          transform: 'translate(-50%, -50%)',
          left: '50%',
          top: '50%',
          textAlign: 'center',
          width: '100%',
          height: '100%',
          display:
            questionStatus === VideoInterviewQuestionStatus.LOADING
              ? 'flex'
              : 'none',
          justifyContent: 'center',
          alignItems: 'center',
          background: '#00000094',
          fontSize: '3rem',
          ['@media (max-width: 820px)']: {
            width: 320,
          },
        }}
      >
        <Timer>{loadingTime}</Timer>
      </Box>
      {/* [FIX #2] SoundMeter now receives the recording stream as a prop instead of
          opening its own competing getUserMedia stream. This prevents the OS from
          multiplexing the microphone or killing the recording stream's audio track. */}
      {isMobileView &&
        deviceIsReady &&
        questionStatus !== VideoInterviewQuestionStatus.STOPPED && (
          <Box
            sx={{
              position: 'absolute',
              bottom: '10px',
              left: '50%',
              transform: 'translateX(calc(-50% - 86px))',
            }}
          >
            <SoundMeter stream={recordingStream} />
          </Box>
        )}
    </VideoRecordWrapper>
  );
};

export default VideoJSComponent;
